import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authApi } from '../../../assets/api/services/authApi';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { savePasscode } from '../../../assets/services/auth';
import { resolveCurrentUserResponse } from '../../../assets/utils/currentUserResponse';
import PinDots from '../PinDots/PinDots';
import PinPage from '../PinPage/PinPage';
import PinPad from '../PinPad/PinPad';
import { usePinEntry } from '../usePinEntry/usePinEntry';
import './ConfirmPasscode.css';

const CONFIRM_PROCESSING_MS = 1000;
const CONFIRM_ERROR_PAUSE_MS = 360;
const MAX_CONFIRM_ATTEMPTS = 5;

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function markPasswordSetInCurrentUser(currentUser) {
  if (!currentUser) {
    return currentUser;
  }

  return {
    ...currentUser,
    reg_data: {
      ...currentUser.reg_data,
      stages: {
        ...currentUser.reg_data?.stages,
        is_phone_verified: true,
        is_password_set: true,
      },
    },
  };
}

function ConfirmPasscode({ authService = authApi }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentUser } = useContext(CurrentUserContext);
  const { pin } = useContext(TranslationContext);
  const passcode = location.state?.passcode;
  const isMountedRef = useRef(true);
  const failedAttemptsRef = useRef(0);
  const [hasError, setHasError] = useState(false);
  const [errorPulse, setErrorPulse] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!passcode) {
      navigate('/onboarding/passcode', { replace: true });
    }
  }, [navigate, passcode]);

  const handleComplete = useCallback(
    async (confirmation) => {
      setIsChecking(true);

      if (confirmation === passcode) {
        setIsProcessing(true);
        await delay(CONFIRM_PROCESSING_MS);

        if (!isMountedRef.current) {
          return true;
        }

        try {
          const nextCurrentUser = await authService.setInitialPassword({ password: passcode });

          if (!isMountedRef.current) {
            return true;
          }

          savePasscode(passcode);
          flushSync(() => {
            setCurrentUser((prevUser) => resolveCurrentUserResponse(
              nextCurrentUser,
              markPasswordSetInCurrentUser(prevUser)
            ));
          });
          navigate('/onboarding/passcode/done', { replace: true });
          return true;
        } catch {
          if (!isMountedRef.current) {
            return false;
          }

          setHasError(true);
          setErrorPulse((value) => value + 1);
          toast.error(pin.setFailed);
          setIsProcessing(false);
          setIsChecking(false);
          return false;
        }
      }

      await delay(CONFIRM_ERROR_PAUSE_MS);

      if (!isMountedRef.current) {
        return false;
      }

      failedAttemptsRef.current += 1;

      if (failedAttemptsRef.current >= MAX_CONFIRM_ATTEMPTS) {
        navigate('/onboarding/passcode', { replace: true });
        return true;
      }

      setHasError(true);
      setErrorPulse((value) => value + 1);
      toast.error(pin.passcodeMismatch);
      setIsProcessing(false);
      setIsChecking(false);
      return false;
    },
    [authService, navigate, passcode, pin, setCurrentUser]
  );

  const { digits, append, remove } = usePinEntry(4, handleComplete);

  const handleDigit = useCallback(
    (digit) => {
      if (hasError) {
        setHasError(false);
      }
      append(digit);
    },
    [append, hasError]
  );

  if (!passcode) {
    return null;
  }

  return (
    <PinPage className="confirm-passcode">
      <div className="pin-page__body">
        <div className="pin-page__intro">
          <h1 className="pin-page__title">{pin.confirmTitle}</h1>
          <p className="pin-page__subtitle">{pin.confirmSubtitle}</p>
        </div>

        <PinDots
          length={4}
          filledCount={digits.length}
          hasError={hasError}
          errorKey={errorPulse}
          isProcessing={isProcessing && digits.length === 4 && !hasError}
          className="pin-dots--with-feedback"
        />
        <PinPad
          onDigit={handleDigit}
          onDelete={remove}
          onBack={() => navigate('/onboarding/passcode')}
          showDelete={digits.length > 0}
          disabled={isChecking}
        />
      </div>
    </PinPage>
  );
}

export default ConfirmPasscode;
